import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";
import style from './profile.module.css'
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/rootReducer';
import { AppContext } from '../../context/AppContextProvider';
import { uploadProfileImage } from '../../../redux/units/actions/units.action';
import { UserObject, updateProfile } from '../../../redux/auth/actions/editProfile.action';
import TinyLoader from '../../common/tiny-loader/TinyLoader';
import Toast from '../../common/toast/Toast';
import PlaceHolder from "../../../images/avatar-placeholder.jpg";
import CameraIcon from '../../svgIcons/CameraIcon';


const Profile = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const { userInfo } = useContext(AppContext);

    // State management
    const [isLoading, setIsLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');

    const {
        handleSubmit,
        formState: { errors, isDirty },
        control,
        reset
    } = useForm({
        defaultValues: {
            fullName: '',
            email: ""
        },
        mode: "onChange",
    });

    // Initialize form with user data from context
    useEffect(() => {
        if (userInfo) {
            reset({
                email: userInfo?.email || '',
                fullName: userInfo?.fullName || ''
            });
            setPreviewUrl(userInfo?.photo_url || '');
        }
    }, [userInfo, reset]);

    // Upload profile picture to server
    const uploadProfilePicture = async (file: File | null): Promise<string> => {
        if (file) {
            const response = await uploadProfileImage(file, '/user-profile');
            return response;
        } else {
            return userInfo?.photo_url || '';
        }
    };

    // Success callback - Update context and localStorage
    const onProfileSuccess = (message: string, user: UserObject) => {
        // Check if photo URL changed
        const imageChanged = userInfo?.photo_url !== user?.photo_url;

        // Update user info in context and localStorage
        const updatedUserInfo = {
            ...userInfo,
            fullName: user?.fullName,
            photo_url: user?.photo_url
        };

        localStorage.setItem('user', JSON.stringify(updatedUserInfo));

        // If image changed, trigger header loading effect
        if (imageChanged) {
            window.dispatchEvent(new Event('profileUpdateStart'));
            setTimeout(() => {
                window.dispatchEvent(new Event('userProfileUpdated'));
            }, 100);
        } else {
            window.dispatchEvent(new Event('userProfileUpdated'));
        }

        // Reset form with updated values
        reset({
            fullName: user?.fullName,
            email: userInfo?.email
        });

        // Clear selected file and update preview
        setSelectedFile(null);
        setPreviewUrl(user?.photo_url);
        setIsLoading(false);
        setShowToast(true);
        setToastMessage(message);
        setToastType('success');
    }

    // Failure callback
    const onProfileFailure = (errMsg: string) => {
        setIsLoading(false);
        setShowToast(true);
        setToastMessage(errMsg);
        setToastType('error');
    }

    // Form submission
    const onSubmit = async (data: { fullName: string }) => {
        setIsLoading(true);

        try {
            const profileURL: string = await uploadProfilePicture(selectedFile);
            const userObj = {
                fullName: data?.fullName,
                photo_url: profileURL
            };

            dispatch(updateProfile(userObj, onProfileSuccess, onProfileFailure));
        } catch (error) {
            onProfileFailure('Failed to upload profile picture. Please try again.');
        }
    };

    // Handle file selection with preview
    const onFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event?.target?.files?.[0];

        if (!file) return;

        // Validate file type
        if (file.type.split('/')[0] !== 'image') {
            setShowToast(true);
            setToastMessage('Please select an image file');
            setToastType('error');
            return;
        }

        // Set selected file and create preview
        setSelectedFile(file);
        setImageLoading(true);

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
            setImageLoading(false);
        };
        reader.readAsDataURL(file);
    };

    // Remove photo - handles both new selection and existing photo
    const handleRemovePhoto = () => {
        if (selectedFile) {
            // User selected a new photo but hasn't saved - just clear selection
            setSelectedFile(null);
            setPreviewUrl(userInfo?.photo_url || '');
        } else if (userInfo?.photo_url) {
            // User wants to remove existing photo - update profile
            setIsLoading(true);
            const userObj = {
                fullName: userInfo?.fullName || '',
                photo_url: '' // Empty string removes the photo
            };
            dispatch(updateProfile(userObj, onProfileSuccess, onProfileFailure));
        }
    };

    // Get current display image
    const getDisplayImage = () => {
        if (previewUrl) return previewUrl;
        if (userInfo?.photo_url) return userInfo.photo_url;
        return PlaceHolder;
    };

    // Check if user has a profile photo
    const hasProfilePhoto = () => {
        return previewUrl || userInfo?.photo_url;
    };

    // Auto-hide toast
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [showToast]);

    return (
        <div className={style.profileContainer}>
            <div className={style.innerContainer}>
                <form onSubmit={handleSubmit(onSubmit)} className={style.formWrapper} noValidate>
                    {/* Header Section */}
                    <div className={style.formHeader}>
                        <h2 className={style.authTitle}>Edit Profile</h2>
                        <p className={style.subtitle}>Update your personal information</p>
                    </div>

                    {/* Profile Image Upload Section */}
                    <div className={style.profileImageSection}>
                        <div className={style.profileImageContainer}>
                            <div className={style.profileImageWrapper}>
                                {/* Shimmer effect while image loads */}
                                {imageLoading && (
                                    <div className={style.imageShimmer}>
                                        <div className={style.shimmer}></div>
                                    </div>
                                )}
                                <img
                                    src={getDisplayImage()}
                                    alt="Profile"
                                    className={`${style.profileImage} ${imageLoading ? style.imageLoading : ''}`}
                                    onLoad={() => setImageLoading(false)}
                                    onError={() => setImageLoading(false)}
                                />
                            </div>
                            <input
                                type='file'
                                id='profile-upload'
                                accept='image/*'
                                onChange={onFileUpload}
                                disabled={isLoading}
                                className={style.fileInput}
                            />
                        </div>

                        <div className={style.imageActions}>
                            <p className={style.imageTitle}>Profile Picture</p>
                            <p className={style.imageDescription}>
                                JPG, PNG or GIF. Max size 5MB
                            </p>

                            <div className={style.photoButtons}>
                                <label
                                    htmlFor='profile-upload'
                                    className={`${style.uploadButton} ${isLoading ? style.buttonDisabled : ''}`}
                                >
                                    <CameraIcon width={18} height={18} color="#51ab86" fill="#fff" />
                                    {selectedFile ? 'Choose Different Photo' : (hasProfilePhoto() ? 'Change Photo' : 'Upload Photo')}
                                </label>

                                {(hasProfilePhoto() || selectedFile) && (
                                    <button
                                        type="button"
                                        className={`${style.removeButton} ${isLoading ? style.buttonDisabled : ''}`}
                                        onClick={handleRemovePhoto}
                                        disabled={isLoading}
                                    >
                                        {selectedFile ? 'Cancel' : 'Remove Photo'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Full Name Input */}
                    <Controller
                        name="fullName"
                        control={control}
                        rules={{
                            required: "Full Name is required",
                            minLength: {
                                value: 2,
                                message: "Full Name must be at least 2 characters"
                            },
                            maxLength: {
                                value: 50,
                                message: "Full Name cannot exceed 50 characters"
                            }
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label className={style.label}>
                                        {/* <UserIcon width={18} height={18} color="#51ab86" /> */}
                                        <span>Full Name</span>
                                    </label>
                                    <div className={style.inputWrapper}>
                                        <input
                                            value={value}
                                            onChange={onChange}
                                            type="text"
                                            autoFocus={false}
                                            id="fullName"
                                            name="fullName"
                                            placeholder="Enter your full name"
                                            disabled={isLoading}
                                            className={errors?.fullName ? style.inputError : ''}
                                        />
                                    </div>
                                    {errors?.fullName && (
                                        <p className={style.errorText}>{errors?.fullName?.message}</p>
                                    )}
                                </div>
                            )
                        }}
                    />

                    {/* Email - Read Only */}
                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { value } }) => {
                            return (
                                <div className={style.fieldWrapper}>
                                    <label className={style.label}>
                                        {/* <EmailIcon width={18} height={18} color="#6b7280" /> */}
                                        <span>Email Address</span>
                                    </label>
                                    <div className={style.inputWrapper}>
                                        <input
                                            value={value}
                                            type="email"
                                            disabled
                                            id="email"
                                            name="email"
                                            placeholder="Your email address"
                                            className={style.disabledInput}
                                        />
                                        {/* <span className={style.lockBadge}>Read Only</span> */}
                                    </div>
                                </div>
                            )
                        }}
                    />

                    {/* Action Buttons */}
                    <div className={style.buttonContainer}>
                        <button
                            type="button"
                            className={style.cancelButton}
                            onClick={() => navigate('/locations')}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className={style.submitButton}
                            disabled={(!isDirty && !selectedFile) || isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <TinyLoader />
                                    <span>Updating...</span>
                                </>
                            ) : (
                                <span>Save Changes</span>
                            )}
                        </button>
                    </div>
                </form>
            </div>
            {showToast && <Toast type={toastType} message={toastMessage} />}
        </div>
    )
}

export default Profile;
